import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  Space,
  Select,
  Input,
  Button,
  Tag,
  Typography,
  Badge,
  Card,
  Row,
  Col,
  Descriptions,
  notification,
  Tabs,
  Switch,
  Tooltip,
} from 'antd';
import { SearchOutlined, ClearOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined, InboxOutlined } from '@ant-design/icons';
import { getResources } from '../api/client';
import { accountLabel } from '../utils/accountNames';

const { Title, Text } = Typography;
const { Option } = Select;

// ─── Constants ────────────────────────────────────────────────────────────────

const REGIONS = ['us-east-1', 'us-east-2', 'us-west-2', 'ap-southeast-1'];

// Values must match the resource_type strings stored in DynamoDB
const RESOURCE_TYPES = ['VPC', 'EC2Instance', 'RDSInstance', 'SecurityGroup', 'AMI', 'EFS', 'EBSVolume'];

const TYPE_LABELS = {
  VPC: 'VPC',
  EC2Instance: 'EC2',
  RDSInstance: 'RDS',
  SecurityGroup: 'Security Group',
  AMI: 'AMI',
  EFS: 'EFS',
  EBSVolume: 'EBS Volume',
};

const TYPE_COLORS = {
  VPC: 'blue',
  EC2Instance: 'green',
  RDSInstance: 'orange',
  SecurityGroup: 'purple',
  AMI: 'cyan',
  EFS: 'geekblue',
  EBSVolume: 'volcano',
};

// Maps an AWS status string to an Ant Design Badge status
const STATUS_TO_BADGE = {
  running: 'success',
  available: 'success',
  stopped: 'error',
  stopping: 'warning',
  pending: 'processing',
  terminated: 'default',
  deleting: 'warning',
  failed: 'error',
  error: 'error',
  deregistered: 'default',
  creating: 'processing',
  updating: 'processing',
  deleted: 'default',
};

// ─── Helper: Export to CSV ────────────────────────────────────────────────────

const csvCell = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;

const exportToCSV = (rows, filters = {}) => {
  const { type, status, accounts = [], regions = [], search, tagKey } = filters;

  // Build a descriptive filename from active filters
  const parts = ['infrastructure'];
  if (type)            parts.push((TYPE_LABELS[type] || type).replace(/\s+/g, '-'));
  if (status)          parts.push(status);
  if (accounts.length) parts.push(accounts.map((a) => accountLabel(a)).join('-'));
  if (regions.length)  parts.push(regions.join('-'));
  if (search)          parts.push(search.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 24));
  if (tagKey)          parts.push(`tag-${tagKey}`.slice(0, 24));
  parts.push(new Date().toISOString().slice(0, 10));

  const filename = parts.join('_').replace(/[^a-zA-Z0-9._-]/g, '-').replace(/-{2,}/g, '-') + '.csv';

  const headers = ['Name', 'Resource ID', 'Type', 'Account', 'Region', 'Status', 'Creation Date', 'Tags', 'Last Updated'];

  const csvRows = [
    headers.join(','),
    ...rows.map((r) => {
      const creationDate =
        r.details?.creation_date || r.details?.launch_time ||
        r.details?.instance_create_time || r.details?.creation_time ||
        r.details?.create_time || '';
      const tags = Object.entries(r.tags || {}).map(([k, v]) => `${k}=${v}`).join('; ');
      return [
        csvCell(r.name || r.resource_id),
        csvCell(r.resource_id),
        TYPE_LABELS[r.resource_type] || r.resource_type || '',
        csvCell(accountLabel(r.account_id)),
        r.region || '',
        r.status || '',
        creationDate ? csvCell(new Date(creationDate).toLocaleString()) : '',
        csvCell(tags),
        r.last_updated ? csvCell(new Date(r.last_updated).toLocaleString()) : '',
      ].join(',');
    }),
  ];

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

// ─── Helper: Export VPC CIDRs to CSV ─────────────────────────────────────────

const exportCIDRsToCSV = (rows) => {
  const headers = ['VPC Name', 'VPC ID', 'CIDR Block', 'Account', 'Region', 'State'];
  const csvRows = [
    headers.join(','),
    ...rows.map((r) =>
      [
        `"${r.name || r.resource_id}"`,
        r.resource_id,
        r.details?.cidr_block || '',
        r.account_id,
        r.region,
        r.details?.state || r.status || '',
      ].join(',')
    ),
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `vpc-cidrs-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// ─── Infrastructure Component ─────────────────────────────────────────────────

const Infrastructure = () => {
  const [loading, setLoading] = useState(false);

  // All resources fetched from the API (unfiltered)
  const [resources, setResources] = useState([]);

  // The subset of resources that pass all active filters
  const [filteredResources, setFilteredResources] = useState([]);

  // Account options come from the fetched data (not hardcoded)
  const [accounts, setAccounts] = useState([]);

  // ── Filter state ────────────────────────────────────────────────────────────
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [tagKey, setTagKey] = useState('');
  const [tagValue, setTagValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Status options derived from resources matching the current type/account/region
  // selection — excludes the status filter itself so the dropdown stays populated.
  const availableStatuses = useMemo(() => {
    let pool = resources;
    if (selectedType) pool = pool.filter((r) => r.resource_type === selectedType);
    if (selectedAccounts.length > 0) pool = pool.filter((r) => selectedAccounts.includes(r.account_id));
    if (selectedRegions.length > 0) pool = pool.filter((r) => selectedRegions.includes(r.region));
    return [...new Set(pool.map((r) => r.status).filter(Boolean))].sort();
  }, [resources, selectedType, selectedAccounts, selectedRegions]);

  // ── VPC CIDR tab state ──────────────────────────────────────────────────────
  const [cidrSearch, setCidrSearch] = useState('');

  // ── Fetch resources (re-runs when showArchived changes) ─────────────────────
  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const params = showArchived ? { include_archived: 'true' } : {};
      const data = await getResources(params);
      const items = data.resources ?? [];
      setResources(items);
      setFilteredResources(items);

      // Derive the list of unique account IDs for the Account filter dropdown
      const uniqueAccounts = [...new Set(items.map((r) => r.account_id).filter(Boolean))];
      setAccounts(uniqueAccounts);
    } catch (err) {
      notification.error({ message: 'Failed to load infrastructure data', description: 'Please try again.', duration: 0 });
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  // ── Apply filters whenever filter state or the resource list changes ─────────
  //
  // This runs on the client side — no extra API calls.
  // useEffect with multiple dependencies re-runs any time any dependency changes.
  //
  useEffect(() => {
    let result = [...resources];

    if (selectedAccounts.length > 0) {
      result = result.filter((r) => selectedAccounts.includes(r.account_id));
    }

    if (selectedRegions.length > 0) {
      result = result.filter((r) => selectedRegions.includes(r.region));
    }

    if (selectedType) {
      result = result.filter((r) => r.resource_type === selectedType);
    }

    if (selectedStatus) {
      result = result.filter((r) => r.status === selectedStatus);
    }

    if (tagKey) {
      const lowerKey = tagKey.toLowerCase();
      result = result.filter((r) =>
        Object.keys(r.tags ?? {}).some((k) => k.toLowerCase().includes(lowerKey))
      );
    }

    if (tagValue) {
      const lowerVal = tagValue.toLowerCase();
      result = result.filter((r) =>
        Object.values(r.tags ?? {}).some((v) =>
          String(v).toLowerCase().includes(lowerVal)
        )
      );
    }

    if (searchText) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (r) =>
          (r.name ?? '').toLowerCase().includes(lower) ||
          (r.resource_id ?? '').toLowerCase().includes(lower)
      );
    }

    setFilteredResources(result);
  }, [resources, selectedAccounts, selectedRegions, selectedType, selectedStatus, tagKey, tagValue, searchText]);

  const handleClearFilters = () => {
    setSelectedAccounts([]);
    setSelectedRegions([]);
    setSelectedType(null);
    setSelectedStatus(null);
    setTagKey('');
    setTagValue('');
    setSearchText('');
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setSelectedStatus(null); // statuses are type-specific; reset when type changes
  };

  // ── Table column definitions ────────────────────────────────────────────────

  const columns = [
    {
      title: 'Name / ID',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || a.resource_id || '').localeCompare(b.name || b.resource_id || ''),
      render: (name, record) => {
        const hasDistinctName = name && name !== record.resource_id;
        return (
          <Space direction="vertical" size={0}>
            {hasDistinctName && <Text strong>{name}</Text>}
            <Text
              type={hasDistinctName ? 'secondary' : undefined}
              strong={!hasDistinctName}
              style={{ fontSize: hasDistinctName ? 12 : undefined }}
            >
              {record.resource_id}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'resource_type',
      key: 'resource_type',
      sorter: (a, b) => (a.resource_type || '').localeCompare(b.resource_type || ''),
      render: (type) => <Tag color={TYPE_COLORS[type] || 'default'}>{TYPE_LABELS[type] ?? type}</Tag>,
    },
    {
      title: 'Account',
      dataIndex: 'account_id',
      key: 'account_id',
      render: (id) => <Text code style={{ fontSize: 12 }}>{accountLabel(id)}</Text>,
    },
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      sorter: (a, b) => (a.region || '').localeCompare(b.region || ''),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      render: (status, record) => {
        if (record.archived) {
          return (
            <Tooltip title={record.archived_at ? `Archived ${new Date(record.archived_at).toLocaleString()}` : 'Archived'}>
              <Space direction="vertical" size={0}>
                <Tag icon={<InboxOutlined />} color="default" style={{ margin: 0 }}>Archived</Tag>
                {status && <Text type="secondary" style={{ fontSize: 11 }}>{status}</Text>}
              </Space>
            </Tooltip>
          );
        }
        return status ? (
          <Badge
            status={STATUS_TO_BADGE[status?.toLowerCase()] || 'default'}
            text={status}
          />
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: 'Creation Date',
      key: 'creation_date',
      sorter: (a, b) => {
        const da = a.details?.creation_date || a.details?.launch_time || a.details?.instance_create_time || a.details?.creation_time || a.details?.create_time || '';
        const db = b.details?.creation_date || b.details?.launch_time || b.details?.instance_create_time || b.details?.creation_time || b.details?.create_time || '';
        return da.localeCompare(db);
      },
      render: (_, record) => {
        const date = record.details?.creation_date
          || record.details?.launch_time
          || record.details?.instance_create_time
          || record.details?.creation_time
          || record.details?.create_time;
        return date
          ? <Text style={{ fontSize: 12 }}>{new Date(date).toLocaleString()}</Text>
          : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags) => {
        if (!tags || Object.keys(tags).length === 0) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Space size={[4, 4]} wrap>
            {Object.entries(tags).map(([k, v]) => (
              <Tag key={k} style={{ fontSize: 11, margin: 0 }}>
                {k}: {v}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_updated',
      key: 'last_updated',
      render: (ts) =>
        ts ? (
          <Text style={{ fontSize: 12 }}>{new Date(ts).toLocaleString()}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  // ── Expandable row ──────────────────────────────────────────────────────────

  const expandedRowRender = (record) => {
    const details = record.details ?? {};

    const toLabel = (key) =>
      key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    const renderValue = (key, value) => {
      if (value === null || value === undefined || value === '') return <Text type="secondary">—</Text>;

      if (typeof value === 'boolean') {
        return value
          ? <Text style={{ color: '#52c41a' }}><CheckCircleOutlined /> Yes</Text>
          : <Text type="secondary"><CloseCircleOutlined /> No</Text>;
      }

      // Timestamp fields
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
        return <Text>{new Date(value).toLocaleString()}</Text>;
      }

      // Security groups array on EC2
      if (key === 'security_groups' && Array.isArray(value)) {
        return (
          <Space wrap>
            {value.map((sg) => (
              <Tag key={sg.sg_id} color="purple">
                {sg.name} ({sg.sg_id})
              </Tag>
            ))}
          </Space>
        );
      }

      // Generic array
      if (Array.isArray(value)) {
        return value.length === 0
          ? <Text type="secondary">—</Text>
          : <Text>{value.join(', ')}</Text>;
      }

      return <Text copyable={typeof value === 'string' && value.length > 8}>{String(value)}</Text>;
    };

    const items = Object.entries(details).map(([key, value]) => ({
      key,
      label: toLabel(key),
      children: renderValue(key, value),
    }));

    return (
      <div style={{ padding: '8px 24px 16px' }}>
        <Descriptions
          bordered
          size="small"
          column={{ xs: 1, sm: 2, md: 3 }}
          items={items}
          labelStyle={{ fontWeight: 500, whiteSpace: 'nowrap', width: 160 }}
        />
      </div>
    );
  };

  // ── VPC CIDR derived data ────────────────────────────────────────────────────

  const allVpcs = resources.filter((r) => r.resource_type === 'VPC');
  const filteredVpcs = cidrSearch
    ? allVpcs.filter((r) => {
        const q = cidrSearch.toLowerCase();
        return (
          (r.details?.cidr_block || '').includes(q) ||
          (r.name || '').toLowerCase().includes(q) ||
          (r.resource_id || '').toLowerCase().includes(q) ||
          (r.account_id || '').includes(q) ||
          (r.region || '').toLowerCase().includes(q)
        );
      })
    : allVpcs;

  const cidrColumns = [
    {
      title: 'VPC',
      key: 'vpc',
      sorter: (a, b) => (a.name || a.resource_id).localeCompare(b.name || b.resource_id),
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          {r.name && r.name !== r.resource_id && <Text strong>{r.name}</Text>}
          <Text type={r.name && r.name !== r.resource_id ? 'secondary' : undefined}
                style={{ fontSize: 12 }} copyable>
            {r.resource_id}
          </Text>
        </Space>
      ),
    },
    {
      title: 'CIDR Block',
      key: 'cidr',
      sorter: (a, b) => (a.details?.cidr_block || '').localeCompare(b.details?.cidr_block || ''),
      render: (_, r) => (
        <Text code copyable style={{ fontSize: 13 }}>
          {r.details?.cidr_block || '—'}
        </Text>
      ),
    },
    {
      title: 'Account',
      dataIndex: 'account_id',
      key: 'account_id',
      sorter: (a, b) => (a.account_id || '').localeCompare(b.account_id || ''),
      render: (id) => <Text code style={{ fontSize: 12 }}>{accountLabel(id)}</Text>,
    },
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      sorter: (a, b) => (a.region || '').localeCompare(b.region || ''),
    },
    {
      title: 'State',
      key: 'state',
      render: (_, r) => {
        const state = r.details?.state || r.status || '';
        return state
          ? <Badge status={state === 'available' ? 'success' : 'default'} text={state} />
          : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Default VPC',
      key: 'is_default',
      render: (_, r) =>
        r.details?.is_default
          ? <Tag color="orange">Default</Tag>
          : <Text type="secondary">No</Text>,
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>

      {/* ── Page Header ────────────────────────────────────────────────── */}
      <Title level={4} style={{ margin: 0 }}>Infrastructure</Title>

      <Tabs
        defaultActiveKey="resources"
        items={[
          {
            key: 'resources',
            label: `All Resources (${filteredResources.length})`,
            children: (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>

      {/* ── Filter Toolbar ──────────────────────────────────────────────── */}
      <Card size="small" title="Filters">
        <Row gutter={[12, 12]} align="bottom">

          <Col xs={24} sm={12} md={6}>
            <Text style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Account</Text>
            <Select
              mode="multiple"
              placeholder="All accounts"
              value={selectedAccounts}
              onChange={setSelectedAccounts}
              style={{ width: '100%' }}
              allowClear
              maxTagCount="responsive"
            >
              {accounts.map((acc) => (
                <Option key={acc} value={acc}>{accountLabel(acc)}</Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Text style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Region</Text>
            <Select
              mode="multiple"
              placeholder="All regions"
              value={selectedRegions}
              onChange={setSelectedRegions}
              style={{ width: '100%' }}
              allowClear
              maxTagCount="responsive"
            >
              {REGIONS.map((r) => (
                <Option key={r} value={r}>{r}</Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Text style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Resource Type</Text>
            <Select
              placeholder="All types"
              value={selectedType}
              onChange={handleTypeChange}
              style={{ width: '100%' }}
              allowClear
            >
              {RESOURCE_TYPES.map((t) => (
                <Option key={t} value={t}>
                  <Tag color={TYPE_COLORS[t]} style={{ margin: 0 }}>{TYPE_LABELS[t] ?? t}</Tag>
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Text style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Status</Text>
            <Select
              placeholder="All statuses"
              value={selectedStatus}
              onChange={setSelectedStatus}
              style={{ width: '100%' }}
              allowClear
              disabled={availableStatuses.length === 0}
            >
              {availableStatuses.map((s) => (
                <Option key={s} value={s}>
                  <Badge status={STATUS_TO_BADGE[s?.toLowerCase()] || 'default'} text={s} />
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Text style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Tag Key</Text>
            <Input
              placeholder="e.g. Environment"
              value={tagKey}
              onChange={(e) => setTagKey(e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Text style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Tag Value</Text>
            <Input
              placeholder="e.g. Production"
              value={tagValue}
              onChange={(e) => setTagValue(e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Text style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Search (Name / ID)</Text>
            <Input
              placeholder="Search by name or resource ID..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={4} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
            <Space>
              <Switch
                size="small"
                checked={showArchived}
                onChange={setShowArchived}
              />
              <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Show Archived</Text>
            </Space>
          </Col>

          <Col xs={24} sm={12} md={3}>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
              style={{ width: '100%' }}
            >
              Clear
            </Button>
          </Col>

        </Row>
      </Card>

      {/* ── Resources Table ─────────────────────────────────────────────── */}
      <Card
        title={
          <Space>
            <Text strong>Resources</Text>
            <Tag>{filteredResources.length.toLocaleString()} results</Tag>
          </Space>
        }
        extra={
          <Button
            icon={<DownloadOutlined />}
            onClick={() => exportToCSV(filteredResources, {
              type: selectedType,
              status: selectedStatus,
              accounts: selectedAccounts,
              regions: selectedRegions,
              search: searchText,
              tagKey,
            })}
            disabled={filteredResources.length === 0}
            size="small"
            style={{ backgroundColor: '#E8741A', borderColor: '#E8741A', color: '#fff' }}
          >
            Export CSV
          </Button>
        }
        styles={{ body: { padding: 0 } }}
      >
        <Table
          dataSource={filteredResources}
          columns={columns}
          rowKey={(r) => `${r.account_id}:${r.resource_id}`}
          loading={loading}
          expandable={{ expandedRowRender }}
          onRow={(record) => ({
            style: record.archived ? { opacity: 0.55 } : {},
          })}
          pagination={{
            pageSize: 50,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `${range[0]}–${range[1]} of ${total} resources`,
          }}
          size="small"
          scroll={{ x: 1000 }}
          locale={{
            emptyText: loading
              ? 'Loading...'
              : 'No resources found. Try adjusting your filters.',
          }}
        />
      </Card>

              </Space>
            ),
          },
          {
            key: 'cidrs',
            label: `VPC CIDRs (${allVpcs.length})`,
            children: (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Search by CIDR, VPC name, ID, account, or region…"
                    value={cidrSearch}
                    onChange={(e) => setCidrSearch(e.target.value)}
                    allowClear
                    style={{ maxWidth: 480 }}
                  />
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => exportCIDRsToCSV(filteredVpcs)}
                    disabled={filteredVpcs.length === 0}
                  >
                    Export CSV
                  </Button>
                  <Text type="secondary" style={{ marginLeft: 'auto' }}>
                    {filteredVpcs.length} of {allVpcs.length} VPCs
                  </Text>
                </div>
                <Table
                  dataSource={filteredVpcs}
                  columns={cidrColumns}
                  rowKey="resource_id"
                  loading={loading}
                  size="small"
                  pagination={{
                    defaultPageSize: 50,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100'],
                    showTotal: (total) => `${total} VPCs`,
                  }}
                  locale={{
                    emptyText: loading ? 'Loading…' : 'No VPCs found.',
                  }}
                />
              </Space>
            ),
          },
        ]}
      />

    </Space>
  );
};

export default Infrastructure;
